/**
 * Cost recovery — the arithmetic.
 *
 * When an owner does not comply and the municipality (through its contractor)
 * does the work itself, what it spent becomes a debt the owner owes (LGA
 * s. 141–143). This module is the one place that debt is computed: the file
 * screen, the municipality's ledger, the generated statement and lien
 * certificate, and the owner's balance on the Client Portal all call it, so
 * the number an owner is shown is the number the clerk certifies.
 *
 * Pure and dependency-free on purpose. Every amount is an integer number of
 * cents; every date is an ISO calendar day (`YYYY-MM-DD`) in the enforcement
 * calendar, never a timestamp, so a payment received at 23:30 in Moncton is not
 * booked to tomorrow by a server running UTC.
 */

// ── Vocabulary ──────────────────────────────────────────────────────────────

/** What a recorded cost is. */
export const COST_CATEGORIES = [
  'work_order',
  'quote',
  'invoice',
  'officer_time',
  'admin_fee',
  'disposal',
  'other',
] as const;
export type CostCategory = (typeof COST_CATEGORIES)[number];

/**
 * Categories recorded for the paper trail but never billed. A quote is what
 * the work was going to cost, not what it did: it supports the work order and
 * the council decision, and billing it as well as the invoice would charge the
 * owner twice.
 */
export const NON_BILLABLE_CATEGORIES: readonly CostCategory[] = ['quote'];

export function isBillableCategory(category: CostCategory): boolean {
  return !NON_BILLABLE_CATEGORIES.includes(category);
}

/**
 * A cost's review state. `pending` is recorded and waiting for a second pair of
 * eyes; only `approved` can be billed. `void` withdraws a cost that was entered
 * in error — the row stays, because a cost that was on the file is part of the
 * record even after it is taken off the bill.
 */
export const COST_ITEM_STATUSES = ['pending', 'approved', 'rejected', 'void'] as const;
export type CostItemStatus = (typeof COST_ITEM_STATUSES)[number];

export const PAYMENT_METHODS = [
  'cheque',
  'cash',
  'etransfer',
  'card',
  /** The Province remitted the amount after it was added to the tax roll. */
  'tax_roll',
  'other',
] as const;
export type PaymentMethod = (typeof PAYMENT_METHODS)[number];

/** How a municipality charges interest on an overdue debt. */
export const INTEREST_METHODS = ['simple', 'compound_monthly'] as const;
export type InterestMethod = (typeof INTEREST_METHODS)[number];

/**
 * What happens to a debt that is not paid.
 *
 * - `tax_roll` — the municipality asks the Department of Finance to add it to
 *   the property's taxes (s. 143). The existing reimbursement request letter is
 *   what asks.
 * - `lien` — the municipality certifies the amount as a lien on the property.
 */
export const ESCALATION_KINDS = ['tax_roll', 'lien'] as const;
export type EscalationKind = (typeof ESCALATION_KINDS)[number];

export const PLAN_FREQUENCIES = ['monthly', 'biweekly'] as const;
export type PlanFrequency = (typeof PLAN_FREQUENCIES)[number];

/** The documents cost recovery generates itself, each in English or French. */
export const COST_DOCUMENT_KINDS = ['cost_statement', 'lien_certificate'] as const;
export type CostDocumentKind = (typeof COST_DOCUMENT_KINDS)[number];

/**
 * Where a file's cost recovery stands, derived — never stored, so it cannot
 * disagree with the money.
 *
 * - `open` — costs are being recorded; nothing has been billed.
 * - `issued` — a statement went out and is not yet due.
 * - `payment_plan` — the owner is paying by instalments and is up to date.
 * - `overdue` — past due (and past any grace), or behind on the plan.
 * - `escalated` — sent to the tax roll or certified as a lien, still unpaid.
 * - `paid` — billed, and nothing is owed.
 */
export const COST_RECOVERY_STATUSES = [
  'open',
  'issued',
  'payment_plan',
  'overdue',
  'escalated',
  'paid',
] as const;
export type CostRecoveryStatus = (typeof COST_RECOVERY_STATUSES)[number];

/** Bounds an operator may set. A rate above 30 % a year is a typo, not a policy. */
export const COST_RECOVERY_LIMITS = {
  maxInterestRateBps: 3000,
  maxGraceDays: 365,
  minPaymentTermsDays: 1,
  maxPaymentTermsDays: 365,
  maxPlanInstallments: 120,
  /** One cost line may not exceed $10,000,000.00 — the column is a 32-bit integer. */
  maxAmountCents: 1_000_000_000,
} as const;

// ── Dates ───────────────────────────────────────────────────────────────────

const DAY_MS = 86_400_000;
const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;

export function isIsoDay(value: unknown): value is string {
  if (typeof value !== 'string' || !ISO_DAY.test(value)) return false;
  const d = new Date(`${value}T00:00:00Z`);
  return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === value;
}

function dayNumber(iso: string): number {
  return Math.round(Date.parse(`${iso}T00:00:00Z`) / DAY_MS);
}

function fromDayNumber(n: number): string {
  return new Date(n * DAY_MS).toISOString().slice(0, 10);
}

export function addDays(iso: string, days: number): string {
  return fromDayNumber(dayNumber(iso) + days);
}

export function daysBetween(fromIso: string, toIso: string): number {
  return dayNumber(toIso) - dayNumber(fromIso);
}

/** The same day `months` later, clamped to the month's end (Jan 31 + 1 → Feb 28). */
export function addMonths(iso: string, months: number): string {
  const [y, m, d] = iso.split('-').map(Number) as [number, number, number];
  const total = y * 12 + (m - 1) + months;
  const year = Math.floor(total / 12);
  const month = total % 12;
  const last = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
  return new Date(Date.UTC(year, month, Math.min(d, last))).toISOString().slice(0, 10);
}

/** First day of each calendar month strictly after `fromIso`, up to and including `toIso`. */
function monthStartsBetween(fromIso: string, toIso: string): string[] {
  const out: string[] = [];
  for (let next = addMonths(`${fromIso.slice(0, 7)}-01`, 1); next <= toIso;) {
    out.push(next);
    next = addMonths(next, 1);
  }
  return out;
}

// ── Money ───────────────────────────────────────────────────────────────────

/** Round to a whole cent, half away from zero. */
export function roundToCent(value: number): number {
  return value < 0 ? -Math.round(-value) : Math.round(value);
}

/**
 * A line's amount from a quantity and a unit price. Quantity is in hundredths
 * so officer time can be 2.25 hours without a float ever being stored.
 */
export function lineTotalCents(quantityHundredths: number, unitAmountCents: number): number {
  if (!Number.isInteger(quantityHundredths) || quantityHundredths <= 0) {
    throw new RangeError(`quantity must be a positive whole number of hundredths`);
  }
  if (!Number.isInteger(unitAmountCents) || unitAmountCents < 0) {
    throw new RangeError(`unit amount must be a non-negative whole number of cents`);
  }
  return roundToCent((quantityHundredths * unitAmountCents) / 100);
}

// ── Interest ────────────────────────────────────────────────────────────────

/**
 * The interest terms a statement was issued under.
 *
 * Copied onto the file when the first statement issues, not read live from the
 * municipality's settings: a council that changes its rate next year has not
 * changed what an owner billed this year agreed to owe.
 */
export interface InterestTerms {
  /** Annual rate in basis points (1 % = 100). Zero charges no interest. */
  rateBps: number;
  method: InterestMethod;
  /** Days after the due date before interest starts. */
  graceDays: number;
}

/** One billed amount: what a statement added to the debt, and when it fell due. */
export interface LedgerCharge {
  id: string;
  amountCents: number;
  dueOn: string;
}

export interface LedgerPayment {
  id: string;
  amountCents: number;
  receivedOn: string;
}

export interface BalanceBreakdown {
  asOn: string;
  /** Everything billed. */
  billedCents: number;
  /** Everything received. */
  paidCents: number;
  /** All interest charged to date, paid or not. */
  interestCents: number;
  /** Of the billed principal, what is still owed. */
  principalOutstandingCents: number;
  /** Of the interest, what is still owed. */
  interestOutstandingCents: number;
  /** What the owner owes today: principal plus interest outstanding. */
  balanceCents: number;
  /** Paid beyond what was owed — a refund the municipality owes the owner. */
  creditCents: number;
  /** Interest one more day adds at today's balance. */
  perDiemCents: number;
  /** The earliest due date with principal still owed, or null. */
  oldestUnpaidDueOn: string | null;
}

interface Bucket {
  /** The day interest starts on this amount. */
  interestFrom: string;
  /** Payment order: the oldest debt is settled first. */
  dueOn: string;
  outstanding: number;
  /** Interest compounded into principal (compound_monthly only). */
  capitalised: boolean;
}

/**
 * The debt on a given day.
 *
 * Interest accrues daily, on a 365-day year, on every amount from the day after
 * its due date plus the grace period. A payment settles interest first and then
 * the oldest principal — the usual order for a municipal receivable, and the one
 * that keeps the arithmetic explainable to an owner at the counter. Under
 * `compound_monthly`, unpaid interest is added to the principal on the first of
 * each month and bears interest from then.
 *
 * Fractions of a cent are carried, not rounded away segment by segment, so a
 * debt left for three years comes out to the cent it would on a calculator.
 */
export function computeBalance(
  charges: readonly LedgerCharge[],
  payments: readonly LedgerPayment[],
  terms: InterestTerms,
  asOn: string,
): BalanceBreakdown {
  const rate = Math.max(0, terms.rateBps) / 10_000;
  const grace = Math.max(0, Math.floor(terms.graceDays));

  // Billed but not yet due still counts as owed — it just bears no interest
  // until its day comes.
  const state: LedgerState = {
    buckets: charges
      .filter((c) => c.amountCents > 0)
      .map((c) => ({
        interestFrom: addDays(c.dueOn, grace),
        dueOn: c.dueOn,
        outstanding: c.amountCents,
        capitalised: false,
      })),
    accrued: 0,
    interestPaid: 0,
    capitalisedTotal: 0,
    credit: 0,
  };
  const inWindow = payments
    .filter((p) => p.amountCents > 0 && p.receivedOn <= asOn)
    .sort((a, b) => a.receivedOn.localeCompare(b.receivedOn) || a.id.localeCompare(b.id));
  const paymentsOn = new Map<string, LedgerPayment[]>();
  for (const p of inWindow)
    paymentsOn.set(p.receivedOn, [...(paymentsOn.get(p.receivedOn) ?? []), p]);

  const timeline = eventDays(state.buckets, inWindow, terms.method, asOn);
  let cursor = timeline[0] ?? asOn;
  for (const day of timeline) {
    // Accrue from the cursor up to (not including) today's events.
    const days = daysBetween(cursor, day);
    if (days > 0 && rate > 0) state.accrued += (bearing(state.buckets, cursor) * rate * days) / 365;
    cursor = day;
    for (const p of paymentsOn.get(day) ?? []) applyPayment(state, p.amountCents);
    if (terms.method === 'compound_monthly' && day.endsWith('-01')) capitalise(state, day);
  }

  const accruedCents = roundToCent(state.accrued);
  const outstanding = (capitalised: boolean) =>
    state.buckets
      .filter((b) => b.capitalised === capitalised)
      .reduce((s, b) => s + b.outstanding, 0);
  const principalOutstandingCents = outstanding(false);
  const interestOutstandingCents = outstanding(true) + accruedCents;
  const oldestUnpaid = state.buckets
    .filter((b) => !b.capitalised && b.outstanding > 0)
    .map((b) => b.dueOn)
    .sort()[0];

  return {
    asOn,
    billedCents: charges.reduce((s, c) => s + Math.max(0, c.amountCents), 0),
    paidCents: inWindow.reduce((s, p) => s + p.amountCents, 0),
    interestCents: state.interestPaid + state.capitalisedTotal + accruedCents,
    principalOutstandingCents,
    interestOutstandingCents,
    balanceCents: principalOutstandingCents + interestOutstandingCents,
    creditCents: state.credit,
    perDiemCents: roundToCent((bearing(state.buckets, addDays(asOn, 1)) * rate) / 365),
    oldestUnpaidDueOn: oldestUnpaid ?? null,
  };
}

interface LedgerState {
  buckets: Bucket[];
  /** Exact interest accrued and neither paid nor capitalised. */
  accrued: number;
  interestPaid: number;
  capitalisedTotal: number;
  credit: number;
}

/** What bears interest on a day: every amount whose interest has started. */
function bearing(buckets: readonly Bucket[], day: string): number {
  return buckets.reduce((s, b) => (b.interestFrom <= day ? s + b.outstanding : s), 0);
}

/** The days anything changes: interest starts, a payment lands, a month turns. */
function eventDays(
  buckets: readonly Bucket[],
  payments: readonly LedgerPayment[],
  method: InterestMethod,
  asOn: string,
): string[] {
  const days = new Set<string>([asOn]);
  for (const b of buckets) if (b.interestFrom <= asOn) days.add(b.interestFrom);
  for (const p of payments) days.add(p.receivedOn);
  const earliest = [...days].sort()[0];
  if (method === 'compound_monthly' && earliest) {
    for (const d of monthStartsBetween(earliest, asOn)) days.add(d);
  }
  return [...days].sort();
}

/** Interest first, then capitalised interest, then the oldest principal. */
function applyPayment(state: LedgerState, amountCents: number): void {
  const toInterest = Math.min(amountCents, roundToCent(state.accrued));
  state.accrued -= toInterest;
  state.interestPaid += toInterest;
  let left = amountCents - toInterest;
  const order = [...state.buckets].sort(
    (a, z) => Number(z.capitalised) - Number(a.capitalised) || a.dueOn.localeCompare(z.dueOn),
  );
  for (const b of order) {
    if (left <= 0) break;
    const take = Math.min(left, b.outstanding);
    b.outstanding -= take;
    left -= take;
  }
  state.credit += left;
}

/** Compounding: unpaid interest joins the principal and bears interest from today. */
function capitalise(state: LedgerState, day: string): void {
  const cap = roundToCent(state.accrued);
  if (cap <= 0) return;
  state.accrued -= cap;
  state.capitalisedTotal += cap;
  state.buckets.push({ interestFrom: day, dueOn: day, outstanding: cap, capitalised: true });
}

// ── Payment plans ───────────────────────────────────────────────────────────

export interface PaymentPlanTerms {
  /** What the plan pays off — the balance on the day it was agreed. */
  totalCents: number;
  installmentCount: number;
  firstDueOn: string;
  frequency: PlanFrequency;
  /** The day it was agreed. Payments from this day count toward it. */
  agreedOn: string;
}

export interface PlanInstallment {
  number: number;
  dueOn: string;
  amountCents: number;
}

/**
 * The instalments, equal but for the last, which absorbs the remainder so the
 * schedule sums to the cent.
 */
export function planSchedule(plan: PaymentPlanTerms): PlanInstallment[] {
  const count = Math.max(1, Math.floor(plan.installmentCount));
  const base = Math.floor(plan.totalCents / count);
  return Array.from({ length: count }, (_, i) => ({
    number: i + 1,
    dueOn:
      plan.frequency === 'monthly'
        ? addMonths(plan.firstDueOn, i)
        : addDays(plan.firstDueOn, i * 14),
    amountCents: i === count - 1 ? plan.totalCents - base * (count - 1) : base,
  }));
}

export interface PlanStanding {
  /** Instalments that have fallen due (past the grace period), in total. */
  dueToDateCents: number;
  /** Received since the plan was agreed. */
  paidSinceAgreedCents: number;
  inGoodStanding: boolean;
  /** The next instalment not yet covered by payments, or null when paid off. */
  next: PlanInstallment | null;
}

export function planStanding(
  plan: PaymentPlanTerms,
  payments: readonly LedgerPayment[],
  asOn: string,
  graceDays: number,
): PlanStanding {
  const schedule = planSchedule(plan);
  const paid = payments
    .filter((p) => p.receivedOn >= plan.agreedOn && p.receivedOn <= asOn)
    .reduce((s, p) => s + p.amountCents, 0);
  const cutoff = addDays(asOn, -Math.max(0, graceDays));
  const due = schedule.filter((i) => i.dueOn <= cutoff).reduce((s, i) => s + i.amountCents, 0);
  let running = 0;
  const next =
    schedule.find((i) => {
      running += i.amountCents;
      return running > paid;
    }) ?? null;
  return { dueToDateCents: due, paidSinceAgreedCents: paid, inGoodStanding: paid >= due, next };
}

// ── Status ──────────────────────────────────────────────────────────────────

export interface StatusInput {
  /** Whether any statement has issued (anything is billed). */
  billed: boolean;
  balance: BalanceBreakdown;
  graceDays: number;
  escalation: EscalationKind | null;
  plan: PaymentPlanTerms | null;
  planStanding: PlanStanding | null;
}

export function deriveStatus(input: StatusInput): CostRecoveryStatus {
  if (!input.billed) return 'open';
  if (input.balance.balanceCents <= 0) return 'paid';
  if (input.escalation) return 'escalated';
  if (input.plan && input.planStanding) {
    return input.planStanding.inGoodStanding ? 'payment_plan' : 'overdue';
  }
  const oldest = input.balance.oldestUnpaidDueOn;
  if (oldest && addDays(oldest, Math.max(0, input.graceDays)) < input.balance.asOn) {
    return 'overdue';
  }
  // Interest alone can be owed after the principal is settled; it is overdue
  // the day it exists, because it only exists on something that was late.
  if (!oldest && input.balance.interestOutstandingCents > 0) return 'overdue';
  return 'issued';
}

/** Days past due for the oldest unpaid charge, or 0. */
export function daysOverdue(balance: BalanceBreakdown): number {
  if (!balance.oldestUnpaidDueOn) return 0;
  return Math.max(0, daysBetween(balance.oldestUnpaidDueOn, balance.asOn));
}

/** Whether an unpaid debt may be escalated today. */
export function canEscalate(status: CostRecoveryStatus): boolean {
  return status === 'overdue';
}

// ── One account, summarised ─────────────────────────────────────────────────

/** The fields of a cost the arithmetic reads — a structural subset of the row. */
export interface AccountItem {
  category: CostCategory;
  status: CostItemStatus;
  amountCents: number;
  billedDocumentId: string | null;
  dueOn: string | null;
}

export interface AccountPayment {
  id: string;
  amountCents: number;
  receivedOn: string;
  voided: boolean;
}

export interface AccountInput {
  items: readonly AccountItem[];
  payments: readonly AccountPayment[];
  terms: InterestTerms;
  plan: PaymentPlanTerms | null;
  escalation: EscalationKind | null;
  asOn: string;
}

export interface AccountSummary {
  status: CostRecoveryStatus;
  balance: BalanceBreakdown;
  planStanding: PlanStanding | null;
  pendingCents: number;
  unbilledCents: number;
  quotedCents: number;
  /** The due date of the oldest unpaid charge, else of the latest statement. */
  dueOn: string | null;
  daysOverdue: number;
  canEscalate: boolean;
}

/**
 * Everything about one file's debt on one day, from its rows.
 *
 * The single entry point the file screen, the ledger and the Client Portal
 * share. Each statement's billed costs become one charge due on that
 * statement's due date; voided payments are left out; quotes never count.
 */
export function summarizeAccount(input: AccountInput): AccountSummary {
  const charges = new Map<string, LedgerCharge>();
  let pendingCents = 0;
  let unbilledCents = 0;
  let quotedCents = 0;
  for (const item of input.items) {
    if (!isBillableCategory(item.category)) {
      if (item.status !== 'void' && item.status !== 'rejected') quotedCents += item.amountCents;
      continue;
    }
    if (item.status === 'pending') pendingCents += item.amountCents;
    if (item.status !== 'approved') continue;
    if (!item.billedDocumentId || !item.dueOn) {
      unbilledCents += item.amountCents;
      continue;
    }
    const key = `${item.billedDocumentId}:${item.dueOn}`;
    const charge = charges.get(key) ?? { id: key, amountCents: 0, dueOn: item.dueOn };
    charge.amountCents += item.amountCents;
    charges.set(key, charge);
  }

  const payments: LedgerPayment[] = input.payments
    .filter((p) => !p.voided)
    .map((p) => ({ id: p.id, amountCents: p.amountCents, receivedOn: p.receivedOn }));
  const balance = computeBalance([...charges.values()], payments, input.terms, input.asOn);
  const standing = input.plan
    ? planStanding(input.plan, payments, input.asOn, input.terms.graceDays)
    : null;
  const status = deriveStatus({
    billed: charges.size > 0,
    balance,
    graceDays: input.terms.graceDays,
    escalation: input.escalation,
    plan: input.plan,
    planStanding: standing,
  });
  const latestDue =
    [...charges.values()]
      .map((c) => c.dueOn)
      .sort()
      .at(-1) ?? null;

  return {
    status,
    balance,
    planStanding: standing,
    pendingCents,
    unbilledCents,
    quotedCents,
    dueOn: balance.oldestUnpaidDueOn ?? latestDue,
    daysOverdue: status === 'overdue' || status === 'escalated' ? daysOverdue(balance) : 0,
    canEscalate: canEscalate(status),
  };
}
